import { z } from 'zod'

/**
 * Curated, CRM-relevant subset of a partner's `partners.branding` jsonb blob.
 * All fields optional — a partner may define none, some, or all. Consumed by
 * the public branding endpoint (backend) and the runtime branding overlay
 * (crm-app). Framework-agnostic so both sides share one contract.
 */
export interface PartnerBranding {
	/**
	 * Tagada-admin entitlement: partner may serve the hub dashboard on a
	 * custom host with their branding. Stored in `partners.branding` jsonb —
	 * NOT derived from `subscribed_products ∋ payfac`. Partners cannot
	 * self-toggle this key (admin `brandingSchema` only).
	 */
	dashboardBranding?: boolean
	/** Absolute logo URL (e.g. object storage / partner-hosted). */
	logoUrl?: string
	/** Absolute dark-mode logo URL. */
	logoDarkUrl?: string
	/** Absolute favicon URL. */
	faviconUrl?: string
	/** Brand primary color (hex, e.g. `#1048b7`). */
	primaryColor?: string
	/** Brand accent / highlight color (hex). */
	accentColor?: string
	/** Product/app display name. */
	appName?: string
	/**
	 * CSS font-family the dashboard should render in. Only takes visual effect
	 * when the value matches a key in the crm-app font registry (which self-hosts
	 * and loads the webfont); otherwise the default face is kept.
	 */
	fontFamily?: string
	/** Dashboard tagline, surfaced as the `<meta name="description">`. */
	appDescription?: string
	/**
	 * Absolute booking URL rendered where the Tagada account-manager / intro-call
	 * link normally sits (sidebar footer, Quick Start header CTA). Any absolute URL
	 * accepted — Cal.com, Calendly, or the partner's own scheduling page. Empty /
	 * missing hides the CTA on partner surfaces.
	 */
	bookCallUrl?: string
}

/**
 * Tolerant parser for the opaque DB `branding` jsonb. Accepts the historical
 * key spellings (`color`/`favicon`) as aliases, ignores unknown keys, and
 * returns `null` when no usable branding field is present so callers can
 * treat "no branding" uniformly.
 */
const RawBrandingSchema = z
	.object({
		dashboardBranding: z.boolean(),
		logoUrl: z.string(),
		logoDarkUrl: z.string(),
		faviconUrl: z.string(),
		favicon: z.string(),
		primaryColor: z.string(),
		color: z.string(),
		accentColor: z.string(),
		appName: z.string(),
		fontFamily: z.string(),
		appDescription: z.string(),
		bookCallUrl: z.string(),
	})
	.partial()
	.passthrough()

export function parsePartnerBranding(raw: unknown): PartnerBranding | null {
	if (!raw || typeof raw !== 'object') return null
	const res = RawBrandingSchema.safeParse(raw)
	if (!res.success) return null
	const r = res.data

	const out: PartnerBranding = {}
	if (r.logoUrl) out.logoUrl = r.logoUrl
	if (r.logoDarkUrl) out.logoDarkUrl = r.logoDarkUrl
	if (r.faviconUrl || r.favicon) out.faviconUrl = r.faviconUrl || r.favicon
	if (r.primaryColor || r.color) out.primaryColor = r.primaryColor || r.color
	if (r.accentColor) out.accentColor = r.accentColor
	if (r.appName) out.appName = r.appName
	if (r.fontFamily) out.fontFamily = r.fontFamily
	if (r.appDescription) out.appDescription = r.appDescription
	if (r.bookCallUrl) out.bookCallUrl = r.bookCallUrl

	return Object.keys(out).length > 0 ? out : null
}

/** Read the admin-set dashboard branding entitlement from raw `partners.branding`. */
export function hasDashboardBrandingEntitlement(raw: unknown): boolean {
	if (!raw || typeof raw !== 'object') return false
	const flag = (raw as Record<string, unknown>).dashboardBranding
	return flag === true
}

/** Keys partners may patch via self-service branding — admin entitlements excluded. */
export const PARTNER_SELF_BRANDING_KEYS = [
	'appName',
	'primaryColor',
	'accentColor',
	'logoUrl',
	'logoDarkUrl',
	'logoMarkUrl',
	'faviconUrl',
	'supportEmail',
	'helpCenterUrl',
	'bookCallUrl',
	'fontFamily',
] as const
